import { v4 as uuid } from "uuid"
import { useDispatch } from "react-redux";
import { getDefaultLayout, getDefaultWidget } from "../components/widgetDashboard/widgetDashboard.helper";
import { getSecurityAsync, getSecurityKey } from "../util/security";
import { Exchange } from "../proto/generated/common";
import { WidgetTypes } from "../components/widgetDashboard/widgetDashboard.enums";
import { updateLayout } from "../reducer/widgetDashboard.reducer";
import { useSelector } from "react-redux";
import { selectAllLayouts } from "../selectors/widgetDashboard";
import { WidgetDashboardConfig } from "../components/widgetDashboard/widgetDashboard.interface";
import { saveUserDashboard } from "../services/apis/dashboard";
import store from "../store";
import { ChartConfig, PaneConfig, Series } from "../components/chartV2/chart.typings";
import { ChartDrawingItem } from "../components/chartV2/drawingTools/drawing.interface";
import { getDefaultChartConfig } from "../util/chart";
import resetPanesConfig from "../pages/home/stockBar/util/resetPanemenu";
import { ChartType } from "../proto/generated/chart";
import { SeriesGroup } from "../components/chartV2/chart.typings";
import { updateChartConfig, updatePanesConfig } from "../reducer/chart.reducer";
import { selectChartConfigs, selectDrawings } from "../selectors/chart";
import { convertExpressionToDisplayExpression } from "../util/expression";
import { setUserDashboards } from "../reducer/user.reducer";
import { DashboardNamespace, DashboardShareStatus, Platform, UserDashboardProto, UserDashboardsProto } from '../proto/generated/user-preference'
import { selectIsMobile } from '../selectors/device'

const useCreateNewLayout = () => {
    const dispatch = useDispatch()
    const layouts = useSelector(selectAllLayouts)

    return (key: string, expressionMode = false, securities?: string[]): { newLayoutId: string, allLayouts: Record<string, WidgetDashboardConfig> } => {
        const singleLayout = uuid().replace(/[-]*/g, "").split("").slice(0, 10).join("")
        const newLayout = getDefaultLayout();
        const securityKey = key || getSecurityKey(Exchange.NSE, `NIFTY_50`);

        newLayout.widgets = {
            1: getDefaultWidget(
                singleLayout, 1, WidgetTypes.CHART, securityKey, expressionMode, securities || [securityKey]
            )
        }

        dispatch(updateLayout({
            id: singleLayout,
            layout: newLayout
        }));

        return { newLayoutId: singleLayout, allLayouts: { ...layouts, [singleLayout]: newLayout } };
    };
}



const useSaveNewLayout = () => {
    const dispatch = useDispatch()
    const layouts = useSelector(selectAllLayouts)
    const configs: Record<string, ChartConfig> = useSelector(selectChartConfigs)
    const drawings: Record<string, ChartDrawingItem[]> = useSelector(selectDrawings)
    const isMobile = useSelector(selectIsMobile)
    return async (key: string, expressionMode = false, securities?: string[], autoSave: boolean = true, namespace: DashboardNamespace = DashboardNamespace.CHART): Promise<{ newLayoutId: string; allLayouts: Record<string, WidgetDashboardConfig>; result : {
        id: string;
        name: string;
        layout: string;
        isDefault: boolean;
        isPredefined: boolean;
    }}> => {
        const singleLayout = uuid().replace(/[-]*/g, "").split("").slice(0, 10).join("")
        const newLayout = getDefaultLayout();
        const securityKey = key || 'NSE:NIFTY_50'

        newLayout.widgets = {
            1: getDefaultWidget(
                singleLayout, 1, WidgetTypes.CHART, securityKey, expressionMode, securities || [securityKey]
            )
        }
        const chartConfigs: Record<string, ChartConfig> = {}
        const chartIds: number[] = Object.keys(newLayout.widgets).map((widgetKey) => newLayout.widgets[parseInt(widgetKey)].slotId)
        const chartDrawings: Record<string, ChartDrawingItem[]> = {}
        chartIds.forEach((id) => {
            const chartId = `${singleLayout}-${id}`
            chartConfigs[chartId] = configs[chartId]
            chartDrawings[chartId] = drawings[chartId] || []
        })
        const layoutToBeSaved = {
            panes: newLayout.panes,
            widgets: newLayout.widgets,
            syncSettings: newLayout.syncSettings,
            charts: { [`${singleLayout}-1`]: getDefaultChartConfig() },
            drawings: chartDrawings
        }
        const expression =  key.split(":")[1]
        const security = await getSecurityAsync(securityKey)
        const name = convertExpressionToDisplayExpression(securityKey , security ? [security] : [])
        const chartPaneConfigs = await resetPanesConfig({ securityList: [securityKey], chartPanes: [], mode: 'SINGLE', expressionToName: new Map([[expression, name]]), expression: expression, comparableSecurities: [], chartType: ChartType.CANDLESTICK, viewKey: `${singleLayout}-1`, chartPanesConfig: {}, chartSeriesGroups: {}, chartSeries: {} })
        if (chartPaneConfigs) {
            dispatch(
                updatePanesConfig({
                    viewKey: `${singleLayout}-1`,
                    panesConfig: Object.values(chartPaneConfigs?.panesConfig),
                    series: Object.values(chartPaneConfigs?.series),
                    seriesGroups: Object.values(chartPaneConfigs?.seriesGroups),
                })
            )
            const panes: string[] = []
            const newPanesConfig: Record<string, PaneConfig> = {}
            const newSeriesGroupConfig: Record<string, SeriesGroup> = {}
            const newSeriesConfig: Record<string, Series> = {}
            chartPaneConfigs.panesConfig.forEach((pane) => {
                panes.push(pane.id)
                newPanesConfig[pane.id] = pane
            })
            chartPaneConfigs.seriesGroups.forEach((sg) => {
                newSeriesGroupConfig[sg.id] = sg
            })

            chartPaneConfigs.series.forEach((s) => {
                newSeriesConfig[s.id] = s
            })
            layoutToBeSaved.charts = {
                [`${singleLayout}-1`]: {
                    ...layoutToBeSaved.charts[`${singleLayout}-1`],
                    panes: panes,
                    panesConfig: newPanesConfig,
                    seriesGroups: newSeriesGroupConfig,
                    series: newSeriesConfig
                }
            };
        }
        let result = {
            id: singleLayout,
            name: 'Unnamed',
            layout: JSON.stringify(layoutToBeSaved),
            isDefault: false,
            isPredefined: false 
        }
        if(autoSave){
            result = await saveUserDashboard({
                dashboard: {
                    id: singleLayout,
                    name: 'Unnamed',
                    layout: JSON.stringify(layoutToBeSaved),
                    isDefault: false,
                    isPredefined: false,
                    shareState: DashboardShareStatus.NOT_SHARED,
                    platform: isMobile ? Platform.MOBILE : Platform.DESKTOP,
                    namespace
                }
            })
        }
        let allDashboards = store.getState().commonStore.userDashBoards || [];
        allDashboards =  [...allDashboards , result as UserDashboardProto] as UserDashboardsProto[]
        dispatch(setUserDashboards(allDashboards))
        dispatch(updateLayout({
            id: singleLayout,
            layout: newLayout
        }));
        dispatch(updateChartConfig({ viewKey: `${singleLayout}-1`, config: layoutToBeSaved.charts[`${singleLayout}-1`] }))
        return { newLayoutId: singleLayout, allLayouts: { ...layouts, [singleLayout]: newLayout } , result : result };
    };
}

export { useCreateNewLayout, useSaveNewLayout };